// Vendor Program v2 PR 2 — TanStack Query hooks over vendorReportsApi.
// Mirrors the useVendorBilling conventions: stable query-key factory +
// mutation invalidation of the owning subtree (reports + compliance share
// one root so a review action refreshes the outstanding chips everywhere).

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  vendorComplaintsApi,
  vendorReportsApi,
} from "@/api/portfolioAdmin/vendorReportsApi";
import type {
  LoanBorrowerLinkDto,
  LoanBrokerLinkDto,
  LoanSellerLinkDto,
  VendorComplaintDto,
  VendorComplaintStatus,
  VendorMonthlyReportDto,
  LoanVendorHistoryEntryDto,
  VendorPerformanceSummaryDto,
  VendorReportComplianceDto,
  VendorReportComplianceParams,
  VendorReportListParams,
  VendorReportReviewDto,
} from "@/api/portfolioAdmin/vendorReportsApi";

export const vendorReportKeys = {
  all: ["portfolio-admin", "vendor-reports"] as const,
  list: (params?: VendorReportListParams) =>
    [
      "portfolio-admin",
      "vendor-reports",
      "list",
      params?.loanId ?? null,
      params?.vendorId ?? null,
      params?.status ?? null,
    ] as const,
  review: (reportId: string) =>
    ["portfolio-admin", "vendor-reports", "review", reportId] as const,
  compliance: (params?: VendorReportComplianceParams) =>
    [
      "portfolio-admin",
      "vendor-reports",
      "compliance",
      params?.loanId ?? null,
      params?.vendorId ?? null,
    ] as const,
  performance: (vendorId: string) =>
    ["portfolio-admin", "vendor-reports", "performance", vendorId] as const,
  // Under the vendor-reports prefix on purpose: an assignment change is what
  // makes a report expected, so the existing invalidations cover it.
  loanHistory: (loanId: string) =>
    ["portfolio-admin", "vendor-reports", "loan-history", loanId] as const,
};

export const vendorComplaintKeys = {
  all: ["portfolio-admin", "vendor-complaints"] as const,
  list: (status?: VendorComplaintStatus) =>
    ["portfolio-admin", "vendor-complaints", "list", status ?? null] as const,
  detail: (complaintId: string) =>
    ["portfolio-admin", "vendor-complaints", "detail", complaintId] as const,
};

export const borrowerLinkKeys = {
  forLoan: (loanId: string) =>
    ["portfolio-admin", "loan", loanId, "borrower-links"] as const,
};

export const sellerLinkKeys = {
  forLoan: (loanId: string) =>
    ["portfolio-admin", "loan", loanId, "seller-links"] as const,
};

export const brokerLinkKeys = {
  forLoan: (loanId: string) =>
    ["portfolio-admin", "loan", loanId, "broker-link"] as const,
};

/** Portal-account picker inside the seller / broker link modals. */
export const partyLinkPickerKeys = {
  search: (party: string, search: string) =>
    ["portal-accounts", "party-link-picker", party, search] as const,
};

export function useVendorReports(
  params?: VendorReportListParams,
  options?: { enabled?: boolean },
) {
  return useQuery<VendorMonthlyReportDto[]>({
    queryKey: vendorReportKeys.list(params),
    queryFn: () => vendorReportsApi.list(params),
    enabled: options?.enabled ?? true,
    // Portal-initiated writes — a vendor submitting a report, a borrower
    // signing one off — never reach the staff query client: the portal uses
    // no React Query and there is no push channel. With the global
    // staleTime of 5 minutes and refetchOnWindowFocus off, a staff tab left
    // open showed stale counts with no way to recover short of a reload.
    // Staff-initiated mutations already invalidate correctly.
    refetchOnWindowFocus: true,
  });
}

export function useVendorReportReview(reportId: string | undefined) {
  return useQuery<VendorReportReviewDto>({
    queryKey: vendorReportKeys.review(reportId ?? ""),
    queryFn: () => vendorReportsApi.review(reportId!),
    enabled: !!reportId,
  });
}

export function useVendorReportCompliance(
  params?: VendorReportComplianceParams,
  options?: { enabled?: boolean },
) {
  return useQuery<VendorReportComplianceDto>({
    queryKey: vendorReportKeys.compliance(params),
    queryFn: () => vendorReportsApi.compliance(params),
    enabled: options?.enabled ?? true,
    // Portal-initiated writes — a vendor submitting a report, a borrower
    // signing one off — never reach the staff query client: the portal uses
    // no React Query and there is no push channel. With the global
    // staleTime of 5 minutes and refetchOnWindowFocus off, a staff tab left
    // open showed stale counts with no way to recover short of a reload.
    // Staff-initiated mutations already invalidate correctly.
    refetchOnWindowFocus: true,
  });
}

/** Step 3 W5 — the loan's vendor-assignment history card. */
export function useLoanVendorHistory(
  loanId: string | undefined,
  options?: { enabled?: boolean },
) {
  return useQuery<LoanVendorHistoryEntryDto[]>({
    queryKey: vendorReportKeys.loanHistory(loanId ?? ""),
    queryFn: () => vendorReportsApi.vendorHistory(loanId!),
    enabled: (options?.enabled ?? true) && !!loanId,
  });
}

export function useVendorPerformance(vendorId: string | undefined) {
  return useQuery<VendorPerformanceSummaryDto>({
    queryKey: vendorReportKeys.performance(vendorId ?? ""),
    queryFn: () => vendorReportsApi.performance(vendorId!),
    enabled: !!vendorId,
  });
}

function useInvalidateVendorReports() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: vendorReportKeys.all });
}

export function useReturnVendorReport(reportId: string) {
  const invalidate = useInvalidateVendorReports();
  return useMutation({
    mutationFn: (body: { comment: string }) =>
      vendorReportsApi.return(reportId, body),
    onSuccess: invalidate,
  });
}

export function useAcceptVendorReport(reportId: string) {
  const invalidate = useInvalidateVendorReports();
  return useMutation({
    mutationFn: () => vendorReportsApi.accept(reportId),
    onSuccess: invalidate,
  });
}

/** Step 3 W2 — staff records the borrower's sign-off on their behalf. */
export function useRecordVendorReportSignOff(reportId: string) {
  const invalidate = useInvalidateVendorReports();
  return useMutation({
    mutationFn: (body: {
      serviceQuality: number;
      responsiveness: number;
      satisfaction: number;
      feedback?: string | null;
    }) => vendorReportsApi.recordSignOff(reportId, body),
    onSuccess: invalidate,
  });
}

// ── complaints ───────────────────────────────────────────────────────────

export function useVendorComplaints(status?: VendorComplaintStatus) {
  return useQuery<VendorComplaintDto[]>({
    queryKey: vendorComplaintKeys.list(status),
    queryFn: () => vendorComplaintsApi.list(status),
  });
}

export function useVendorComplaint(complaintId: string | undefined) {
  return useQuery<VendorComplaintDto>({
    queryKey: vendorComplaintKeys.detail(complaintId ?? ""),
    queryFn: () => vendorComplaintsApi.get(complaintId!),
    enabled: !!complaintId,
  });
}

function useInvalidateVendorComplaints() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: vendorComplaintKeys.all });
}

export function useAcknowledgeComplaint(complaintId: string) {
  const invalidate = useInvalidateVendorComplaints();
  return useMutation({
    mutationFn: () => vendorComplaintsApi.acknowledge(complaintId),
    onSuccess: invalidate,
  });
}

export function useResolveComplaint(complaintId: string) {
  const invalidate = useInvalidateVendorComplaints();
  return useMutation({
    mutationFn: (body: { reason: string }) =>
      vendorComplaintsApi.resolve(complaintId, body),
    onSuccess: invalidate,
  });
}

export function useHandOffComplaint(complaintId: string) {
  const invalidate = useInvalidateVendorComplaints();
  return useMutation({
    mutationFn: (body: { suggestedSeverity?: string | null }) =>
      vendorComplaintsApi.handoff(complaintId, body),
    onSuccess: invalidate,
  });
}

// ── borrower portal links (loan Parties tab) ─────────────────────────────

export function useLoanBorrowerLinks(
  loanId: string | undefined,
  options?: { enabled?: boolean },
) {
  return useQuery<LoanBorrowerLinkDto[]>({
    queryKey: borrowerLinkKeys.forLoan(loanId ?? ""),
    queryFn: () => vendorReportsApi.borrowerLinks(loanId!),
    enabled: !!loanId && (options?.enabled ?? true),
  });
}

export function useLinkBorrower(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (portalUserId: string) =>
      vendorReportsApi.linkBorrower(loanId, portalUserId),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: borrowerLinkKeys.forLoan(loanId) }),
  });
}

// ── seller portal links (loan Parties tab) ───────────────────────────────

export function useLoanSellerLinks(
  loanId: string | undefined,
  options?: { enabled?: boolean },
) {
  return useQuery<LoanSellerLinkDto[]>({
    queryKey: sellerLinkKeys.forLoan(loanId ?? ""),
    queryFn: () => vendorReportsApi.sellerLinks(loanId!),
    enabled: !!loanId && (options?.enabled ?? true),
  });
}

export function useLinkSeller(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (portalUserId: string) =>
      vendorReportsApi.linkSeller(loanId, portalUserId),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: sellerLinkKeys.forLoan(loanId) }),
  });
}

// ── broker portal link (loan Parties tab) ────────────────────────────────

export function useLoanBrokerLink(
  loanId: string | undefined,
  options?: { enabled?: boolean },
) {
  return useQuery<LoanBrokerLinkDto | null>({
    queryKey: brokerLinkKeys.forLoan(loanId ?? ""),
    queryFn: () => vendorReportsApi.brokerLink(loanId!),
    enabled: !!loanId && (options?.enabled ?? true),
  });
}

export function useLinkBroker(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (portalUserId: string) =>
      vendorReportsApi.linkBroker(loanId, portalUserId),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: brokerLinkKeys.forLoan(loanId) }),
  });
}
